import {ViewIdentifiable} from "../../models/common/ViewSettings";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {ApiReturnType, getUserViewSettingsApiRequest} from "../../models/common/ApiReturnType";
import {AppState} from "../../reducers/RootReducer";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {eventNotificationError, eventNotificationSuccess} from "../common/EventNotificationActions";
import {
    Contractor,
    ContractorId, ContractorProp, ContractorRequest, ContractorRequestProp, ContractorModule
} from "../../models/contractor/Contractor";

import {  ofType, StateObservable } from "redux-observable";
import { switchMap, map, catchError } from "rxjs/operators";
import { ajax } from "rxjs/ajax";
import { Observable, of } from 'rxjs';
import {Nullable} from "../../models/common/Nullable";
import {Identifier, makeNumericId} from "../../models/common/Identifier";
import {RouteConfig, RouteMethods} from "../../config/RouteConfig";
import {sendUserToPage} from "../common/UserViewSettingsActions";
import {fetchContractedWorks} from "./ContractedWorkActions";
import {fetchContractorContacts} from "./ContractorContactActions";

/**
 * base API path to interact with project roles
 */
function apiPath( id: Nullable<number> = null ): string {
    return APP_CONFIG.baseWeb + "contractors/"+ ( id !== null ? id : '');
}

/** union of all project role actions types */
export type ContractorActionTypes = FetchContractorsActionTypes
    | FetchContractorByIdActionTypes
    | CreateContractorActionTypes
    | UpdateContractorActionTypes
    | DeleteContractorActionTypes
    | RestoreContractorActionTypes

/**
 * FETCH CONTRACTORS BY USER VIEW SETTINGS
 *
 * user view settings will automatically be retrieved from redux store
 */
// TYPESCRIPT REDUX ACTION TYPES
export interface FetchContractorsAction extends ViewIdentifiable, ReduxAction< typeof FETCH_CONTRACTORS > {}
export interface FetchContractorsSuccessAction extends ViewIdentifiable, ReduxAction<typeof FETCH_CONTRACTORS_SUCCESS > {
    payload: ApiReturnType< Contractor[] >
}
export interface FetchContractorsErrorAction extends ViewIdentifiable, ErrorAction< typeof FETCH_CONTRACTORS_ERROR >{}

export type FetchContractorsActionTypes = FetchContractorsAction
    | FetchContractorsErrorAction
    | FetchContractorsSuccessAction

// REDUX ACTION TYPE CONSTANTS
export const FETCH_CONTRACTORS = "FETCH_CONTRACTORS";
export const FETCH_CONTRACTORS_SUCCESS = "FETCH_CONTRACTORS_SUCCESS";
export const FETCH_CONTRACTORS_ERROR = "FETCH_CONTRACTORS_ERROR";

// REDUX ACTION ACCESSORS FUNCTIONS
export function fetchContractors( viewIdentifier: string ) : FetchContractorsAction {
    return { type: FETCH_CONTRACTORS, viewIdentifier };
}
export function fetchContractorsSuccess(
    viewIdentifier: string,
    payload: ApiReturnType< Contractor[] >
) : FetchContractorsSuccessAction
{
    return { type: FETCH_CONTRACTORS_SUCCESS, viewIdentifier, payload };
}
export function fetchContractorsError( viewIdentifier: string, error: Error ): FetchContractorsErrorAction {
    return { type: FETCH_CONTRACTORS_ERROR, viewIdentifier, error };
}

// REDUX OBSERVABLE EPIC
export function fetchContractorsEpic(
    action$: Observable< FetchContractorsAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_CONTRACTORS ),
        switchMap( action => {
            const request = getUserViewSettingsApiRequest(
                apiPath(),
                action.viewIdentifier,
                store$
            );

            return ajax( APP_CONFIG.getRequestObject(request.path) ).pipe(
                map( response => fetchContractorsSuccess(
                    action.viewIdentifier,
                    response.response as ApiReturnType<Contractor[]>
                ) ),
                catchError(error => of(
                    fetchContractorsError(action.viewIdentifier, error),
                    eventNotificationError(error, store$, action.viewIdentifier, "fetchContractors")
                ))
            );

        })
    );
}

/**
 * FETCH SINGLE CONTRACTOR
 */
export interface FetchContractorByIdAction extends ContractorId, ReduxAction< typeof FETCH_CONTRACTOR_BY_ID> {

}
export interface FetchContractorByIdSuccessAction extends ContractorProp, ReduxAction< typeof FETCH_CONTRACTOR_BY_ID_SUCCESS> {

}
export interface FetchContractorByIdErrorAction extends ContractorId, ErrorAction< typeof FETCH_CONTRACTOR_BY_ID_ERROR> {

}
//union of action to fetch by id
export type FetchContractorByIdActionTypes = FetchContractorByIdAction
    | FetchContractorByIdSuccessAction
    | FetchContractorByIdErrorAction

// REDUX ACTION TYPE CONSTANTS
export const FETCH_CONTRACTOR_BY_ID = "FETCH_CONTRACTOR_BY_ID";
export const FETCH_CONTRACTOR_BY_ID_SUCCESS = "FETCH_CONTRACTOR_BY_ID_SUCCESS";
export const FETCH_CONTRACTOR_BY_ID_ERROR = "FETCH_CONTRACTOR_BY_ID_ERROR";

//Redux action accessors function
export function fetchContractorById( contractorId: Identifier<number> ): FetchContractorByIdAction {
    return { type: FETCH_CONTRACTOR_BY_ID, contractorId };
}
export function fetchContractorByIdSuccess( contractor: Contractor ): FetchContractorByIdSuccessAction {
    return { type: FETCH_CONTRACTOR_BY_ID_SUCCESS, contractor };
}
export function fetchContractorByIdError( error: any, contractorId: Identifier<number> ): FetchContractorByIdErrorAction {
    return { type: FETCH_CONTRACTOR_BY_ID_ERROR, error, contractorId };
}

//redux observable epic as bridge to rxjs
export function fetchContractorByIdEpic(
    action$: Observable< FetchContractorByIdAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_CONTRACTOR_BY_ID ),
        switchMap( action => {

                return ajax(
                    APP_CONFIG.getRequestObject( apiPath( action.contractorId.rawValue ) )
                ).pipe(
                    map( response => fetchContractorByIdSuccess( response.response as Contractor ) ),
                    catchError( error => of(
                        fetchContractorByIdError( error, action.contractorId ),
                        eventNotificationError( error, store$, ContractorModule.identifier, "fetchContractorById" )
                    ) )
                )
            }
        )
    );
}

/**
 * MUTATING REQUESTS
 */

/**
 * api model does not accept null for offices and contacts for create and update, null will be changed to empty array
 *
 */
function toApiFormat( contractorRequest: ContractorRequest ): ContractorRequest {
    return {
        ...contractorRequest,
        contractedWorks: contractorRequest.contractedWorks !== null ? contractorRequest.contractedWorks : [],
        offices: contractorRequest.offices !== null ? contractorRequest.offices : [],
        contacts: contractorRequest.contacts !== null ? contractorRequest.contacts : []
    };
}

/**
 * CREATE CONTRACTOR
 */
export interface CreateContractorAction extends ContractorRequestProp, ReduxAction<typeof CREATE_CONTRACTOR> {

}
export interface CreateContractorSuccessAction extends ContractorId, ReduxAction<typeof CREATE_CONTRACTOR_SUCCESS> {

}
export interface CreateContractorErrorAction extends ErrorAction<typeof CREATE_CONTRACTOR_ERROR> {

}
// typescript union type of create actions
export type CreateContractorActionTypes = CreateContractorAction
    | CreateContractorSuccessAction
    | CreateContractorErrorAction

//redux action constants
export const CREATE_CONTRACTOR = "CREATE_CONTRACTOR";
export const CREATE_CONTRACTOR_SUCCESS = "CREATE_CONTRACTOR_SUCCESS";
export const CREATE_CONTRACTOR_ERROR = "CREATE_CONTRACTOR_ERROR";

//redux action accessors functions
export function createContractor( contractorRequest: ContractorRequest ): CreateContractorAction {
    return { type: CREATE_CONTRACTOR,  contractorRequest };
}
export function createContractorSuccess( contractorId: Identifier<number> ): CreateContractorSuccessAction {
    return { type: CREATE_CONTRACTOR_SUCCESS, contractorId };
}
export function createContractorError( error: any ): CreateContractorErrorAction {
    return { type: CREATE_CONTRACTOR_ERROR, error };
}

//redux observable rxjs bridge
export function createContractorEpic(
    action$: Observable< CreateContractorAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( CREATE_CONTRACTOR ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath(),
                    RouteMethods.post,
                    toApiFormat( action.contractorRequest )
                )
            ).pipe(
                switchMap( response => of(
                    createContractorSuccess( ( response.response as ContractorId ).contractorId ),
                    fetchContractors( ContractorModule.identifier ),
                    eventNotificationSuccess(),
                    sendUserToPage(
                        RouteConfig.contractors.routes.main.path,
                        ContractorModule.identifier
                    )
                ) ),
                catchError( error => of(
                    createContractorError( error ),
                    eventNotificationError( error, store$, ContractorModule.identifier, "createContractor" )
                ))
            )
        )
    )
}

/**
 * UPDATE CONTRACTOR
 */
export interface UpdateContractorAction extends ContractorRequestProp, ReduxAction<typeof UPDATE_CONTRACTOR> {

}
export interface UpdateContractorSuccessAction extends ContractorId, ReduxAction<typeof UPDATE_CONTRACTOR_SUCCESS> {

}
export interface UpdateContractorErrorAction extends ErrorAction<typeof UPDATE_CONTRACTOR_ERROR> {

}
// typescript union type of update actions
export type UpdateContractorActionTypes = UpdateContractorAction
    | UpdateContractorSuccessAction
    | UpdateContractorErrorAction

//redux action constants
export const UPDATE_CONTRACTOR = "UPDATE_CONTRACTOR";
export const UPDATE_CONTRACTOR_SUCCESS = "UPDATE_CONTRACTOR_SUCCESS";
export const UPDATE_CONTRACTOR_ERROR = "UPDATE_CONTRACTOR_ERROR";

//redux action accessors functions
export function updateContractor( contractorRequest: ContractorRequest ): UpdateContractorAction {
    return { type: UPDATE_CONTRACTOR, contractorRequest };
}
export function updateContractorSuccess( contractorId: Identifier<number> ): UpdateContractorSuccessAction {
    return { type: UPDATE_CONTRACTOR_SUCCESS, contractorId };
}
export function updateContractorError( error: any ): UpdateContractorErrorAction {
    return { type: UPDATE_CONTRACTOR_ERROR, error };
}

//redux observable rxjs bridge
export function updateContractorEpic(
    action$: Observable< UpdateContractorAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( UPDATE_CONTRACTOR ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.contractorRequest.id ),
                    RouteMethods.put,
                    toApiFormat( action.contractorRequest )
                )
            ).pipe(
                switchMap( response => of(
                    updateContractorSuccess( ( response.response as ContractorId ).contractorId ),
                    fetchContractors( ContractorModule.identifier ),
                    fetchContractedWorks(
                        makeNumericId( action.contractorRequest.id ),
                        ContractorModule.identifier
                    ),
                    fetchContractorById( makeNumericId( action.contractorRequest.id ) ),
                    fetchContractorContacts(
                        makeNumericId( action.contractorRequest.id ),
                        ContractorModule.identifier
                    ),
                    eventNotificationSuccess(),
                    sendUserToPage(
                        RouteConfig.contractors.routes.main.path,
                        ContractorModule.identifier
                    )
                ) ),
                catchError( error => of(
                    updateContractorError( error ),
                    eventNotificationError( error, store$, ContractorModule.identifier, "updateContractor" )
                ))
            )
        )
    )
}

/**
 * DELETE CONTRACTOR
 */
export interface DeleteContractorAction extends ContractorId, ReduxAction<typeof DELETE_CONTRACTOR> {

}
export interface DeleteContractorSuccessAction extends ContractorId, ReduxAction<typeof DELETE_CONTRACTOR_SUCCESS> {

}
export interface DeleteContractorErrorAction extends ErrorAction<typeof DELETE_CONTRACTOR_ERROR> {

}
// typescript union type of delete actions
export type DeleteContractorActionTypes = DeleteContractorAction
    | DeleteContractorSuccessAction
    | DeleteContractorErrorAction

//redux action constants
export const DELETE_CONTRACTOR = "DELETE_CONTRACTOR";
export const DELETE_CONTRACTOR_SUCCESS = "DELETE_CONTRACTOR_SUCCESS";
export const DELETE_CONTRACTOR_ERROR = "DELETE_CONTRACTOR_ERROR";

//redux action accessors functions
export function deleteContractor( contractorId: Identifier<number>  ): DeleteContractorAction {
    return { type: DELETE_CONTRACTOR, contractorId };
}
export function deleteContractorSuccess( contractorId: Identifier<number> ): DeleteContractorSuccessAction {
    return { type: DELETE_CONTRACTOR_SUCCESS, contractorId };
}
export function deleteContractorError( error: any ): DeleteContractorErrorAction {
    return { type: DELETE_CONTRACTOR_ERROR, error };
}

//redux observable rxjs bridge
export function deleteContractorEpic(
    action$: Observable< DeleteContractorAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( DELETE_CONTRACTOR ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.contractorId.rawValue ),
                    RouteMethods.delete
                )
            ).pipe(
                switchMap( _ => of(
                    deleteContractorSuccess( action.contractorId ),
                    eventNotificationSuccess(),
                    fetchContractorById( action.contractorId )
                ) ),
                catchError( error => of(
                    deleteContractorError( error ),
                    eventNotificationError( error, store$, ContractorModule.identifier, "deleteContractor" )
                ))
            )
        )
    )
}

/**
 * RESTORE CONTRACTOR
 */
export interface RestoreContractorAction extends ContractorId, ReduxAction<typeof RESTORE_CONTRACTOR> {

}
export interface RestoreContractorSuccessAction extends ContractorId, ReduxAction<typeof RESTORE_CONTRACTOR_SUCCESS> {

}
export interface RestoreContractorErrorAction extends ErrorAction<typeof RESTORE_CONTRACTOR_ERROR> {

}
// typescript union type of restore actions
export type RestoreContractorActionTypes = RestoreContractorAction
    | RestoreContractorSuccessAction
    | RestoreContractorErrorAction

//redux action constants
export const RESTORE_CONTRACTOR = "RESTORE_CONTRACTOR";
export const RESTORE_CONTRACTOR_SUCCESS = "RESTORE_CONTRACTOR_SUCCESS";
export const RESTORE_CONTRACTOR_ERROR = "RESTORE_CONTRACTOR_ERROR";

//redux action accessors functions
export function restoreContractor( contractorId: Identifier<number>  ): RestoreContractorAction {
    return { type: RESTORE_CONTRACTOR, contractorId };
}
export function restoreContractorSuccess( contractorId: Identifier<number> ): RestoreContractorSuccessAction {
    return { type: RESTORE_CONTRACTOR_SUCCESS, contractorId };
}
export function restoreContractorError( error: any ): RestoreContractorErrorAction {
    return { type: RESTORE_CONTRACTOR_ERROR, error };
}

//redux observable rxjs bridge
export function restoreContractorEpic(
    action$: Observable< RestoreContractorAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( RESTORE_CONTRACTOR ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.contractorId.rawValue ),
                    RouteMethods.patch
                )
            ).pipe(
                switchMap( _ => of(
                    restoreContractorSuccess( action.contractorId ),
                    eventNotificationSuccess(),
                    fetchContractorById( action.contractorId )
                ) ),
                catchError( error => of(
                    restoreContractorError( error ),
                    eventNotificationError( error, store$, ContractorModule.identifier, "restoreContractor" )
                ))
            )
        )
    )
}